import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock } from "vue";
import { watch } from 'vue';
import AddEmailModal from '@/shared/components/channels/AddEmailModal.vue';
import { useOrganisationSearch } from '@/modules/organisation/public';
import { SHARED_EMAIL_DISABLED_ERROR_CODE, useSharedEmailSetting } from '@/modules/workspace/public';
import { addContactEmail, checkContactDuplicates } from '../../infrastructure/adapters/contactService';
import { autoCreateOrganisationRelation } from '../../infrastructure/api/contactRelationsApi';
import { logger } from '@/infrastructure/services/LoggerService';
export default /*@__PURE__*/ _defineComponent({
    __name: 'ContactAddEmailModal',
    props: /*@__PURE__*/ _mergeModels({
        contactId: {},
        hasPrimaryEmail: { type: Boolean },
        contactName: {},
        existingRelationOrgIds: {}
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["created"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        /**
         * Contact binding of the reusable {@link AddEmailModal} (OP-5603 / US-06).
         *
         * Injects the contact-specific persistence (`addContactEmail`) and the
         * "email already used by another contact" detection (`checkContactDuplicates` /
         * `emailExactMatch`, excluding the current contact). Runs in context mode so the
         * Personnel/Professionnel toggle + organisation anchor (OP-5646) are shown.
         */
        const organisationSearch = useOrganisationSearch();
        const props = __props;
        const open = _useModel(__props, "modelValue");
        const { sharedEmailsEnabled, sharedEmailSettingPending, loadSharedEmailSetting, onEmailWriteRejected } = useSharedEmailSetting();
        watch(open, (isOpen) => { if (isOpen)
            void loadSharedEmailSetting(); }, { immediate: true });
        const emit = __emit;
        // errorCode → i18n key (R70). The three CHANNEL_* codes are wire contracts guarded
        // client-side (context radio + required organisation for OP-5646; the 255-char
        // length check in useAddEmailForm for the value), so they only fire as
        // defense-in-depth. `SharedEmail.Disabled` is reachable: the setting can be turned
        // off between the modal opening and the save. `CommunicationChannel.Email.Invalid` (blocked TLDs, OP-7457)
        // is mapped by the shared modal's built-in defaults (channelErrorOverrides.ts);
        // the generic fallback covers everything else.
        const ERROR_OVERRIDES = {
            CHANNEL_USAGE_CONTACT_ONLY: 'contact.detail.addEmail.errors.usageInvalid',
            CHANNEL_LINKED_ORG_REQUIRES_PROFESSIONAL: 'contact.detail.addEmail.errors.linkedOrgRequiresProfessional',
            CHANNEL_VALUE_TOO_LONG: 'contact.detail.addEmail.validation.emailTooLong',
            [SHARED_EMAIL_DISABLED_ERROR_CODE]: 'contact.detail.addEmail.errors.sharedEmailsDisabled',
            // A duplicate the debounced lookup missed (it failed, or Save beat it).
            'CommunicationChannel.Email.AlreadyExists': 'contact.detail.addEmail.validation.emailAlreadyUsed',
        };
        async function submitEmail(payload) {
            const channelId = await addContactEmail(props.contactId, {
                value: payload.value,
                isPrimary: payload.isPrimary,
                // `withContext` guarantees usage is populated; fall back defensively.
                usage: payload.usage ?? 'Professional',
                linkedOrganisationId: payload.linkedOrganisationId,
                isShared: payload.isShared,
            }).catch((error) => {
                onEmailWriteRejected(error);
                throw error;
            });
            // OP-5603 amendment (09/06/2026): auto-create a contact↔organisation relation when
            // a Professional email is anchored to an organisation.
            // Best-effort: a failure must not surface to the user — the channel was already persisted.
            if (payload.usage === 'Professional' && payload.linkedOrganisationId) {
                try {
                    await autoCreateOrganisationRelation(props.contactId, payload.linkedOrganisationId);
                }
                catch (err) {
                    logger.error(`Auto-relation creation failed after email channel save for contact ${props.contactId}`, err);
                }
            }
            return channelId;
        }
        // Returns the full names (prénom + nom) of OTHER contacts holding an email that
        // exactly matches the typed address. A non-empty list reveals the "Email associé"
        // popover and, when sharing is enabled, the shared opt-in; an empty list means the
        // address is free.
        async function checkAlreadyUsed(email) {
            const matches = await checkContactDuplicates({ email });
            return matches
                .filter((m) => m.emailExactMatch && m.id !== props.contactId)
                .map((m) => m.fullName);
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(AddEmailModal, {
                modelValue: open.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((open).value = $event)),
                "has-primary-email": __props.hasPrimaryEmail,
                "with-context": true,
                "organisation-search": _unref(organisationSearch),
                "owner-name": __props.contactName,
                submit: submitEmail,
                "check-already-used": checkAlreadyUsed,
                "error-overrides": ERROR_OVERRIDES,
                "existing-relation-org-ids": __props.existingRelationOrgIds,
                "shared-emails-enabled": _unref(sharedEmailsEnabled),
                "shared-email-setting-pending": _unref(sharedEmailSettingPending),
                "i18n-prefix": "contact.detail.addEmail",
                onCreated: _cache[1] || (_cache[1] = ($event) => (emit('created')))
            }, null, 8 /* PROPS */, ["modelValue", "has-primary-email", "organisation-search", "owner-name", "existing-relation-org-ids", "shared-emails-enabled", "shared-email-setting-pending"]));
        };
    }
});
